import React, {useState, useRef, useCallback, useEffect} from 'react';
import {Link} from 'react-router-dom';
import Form, {
  Item,
  Label,
  ButtonItem,
  ButtonOptions,
  RequiredRule,
  EmailRule
} from 'devextreme-react/form';
import LoadIndicator from 'devextreme-react/load-indicator';
import notify from 'devextreme/ui/notify';
import {useAuth} from '../../contexts/auth';
import {MsalProvider, useMsal} from '@azure/msal-react';
import msalInstance from '../../api/authConfig';
import './LoginForm.scss';
import {Button} from "devextreme-react/button";
import Accordion, {Item as AccordItem} from 'devextreme-react/accordion';
import TabPanel from 'devextreme-react/tab-panel';

const tabsDataSource = [
  {title: 'Active Directory SSO', icon: 'activefolder'},
  {title: 'Custom Credentials', icon: 'user'},
];

const LoginButton = () => {
  const {instance, accounts} = useMsal();
  const {ssoLoginOrRegister} = useAuth();
  const [activeAccount, setActiveAccount] = useState(null);

  useEffect(() => {
    // Set the active account if one exists
    if (accounts.length > 0) {
      setActiveAccount(instance.getActiveAccount() || accounts[0]); // Default to first account
      instance.setActiveAccount(accounts[0]); // Persist selection
    }
  }, [accounts, instance]);

  const handleLogin = () => {
    instance.loginPopup({
      scopes: [
        "api://9e4256f3-c154-4215-a43c-3c2fc985d115/rw-scope",
        //"Files.Read",
        //"Sites.Read.All"
      ],
    }).then(response => {
      console.log('MSAL response', response);
      // Set the active account after login
      setActiveAccount(response.account);
      instance.setActiveAccount(response.account);

      // const accessToken = response.accessToken;
      // Store access token and use it for API requests
      ssoLoginOrRegister(response).then(result => {
        console.log('SSO login result', result);
        if (!result.isOk) {
          notify(result.message, 'error', 2000);
        }
        if (result.isOk) window.location.reload();
      });
    }).catch(error => {
      console.error(error);
    });
  };

  return (
    <div>
      {activeAccount && <p>Welcome, {activeAccount.username}!</p>}
      <Button type="default"
              stylingMode="contained"
              text="Sign in with Azure AD"
              onClick={handleLogin}>
      </Button>
    </div>
  );
};

export default function LoginForm() {
  const {signIn} = useAuth();
  const [loading, setLoading] = useState(false);
  const formData = useRef({email: '', password: ''});

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    const {email, password} = formData.current;
    setLoading(true);

    const result = await signIn(email, password);

    if (!result.isOk) {
      notify(result.message, 'error', 2000);
    }
    setLoading(false);
    if (result.isOk) window.location.reload();
  }, [signIn]);

  // const onCreateAccountClick = useCallback(() => {
  //   navigate('/create-account');
  // }, [navigate]);

  function TabPanelItem(e) {
    console.log('TabPanelItem', e);
    switch (e.index) {
      case 0: // Access
        return <div className="login-tab-page vertical-paddings">
          <p>
            Sign in with your Azure AD account. The application will use your account to authenticate you. You will be
            redirected to the Azure AD login page.
          </p>
          <MsalProvider instance={msalInstance}>
            <LoginButton/>
          </MsalProvider>
        </div>
      case 1:
        return <div className="login-tab-page vertical-paddings">
          <p>
            Sign in with custom credentials. Use this option if you are a system administrator or user without Azure AD
            accounts. You need to provide your email and password.
          </p>
          <form className={'login-form'} onSubmit={onSubmit}>
            <Form formData={formData.current} disabled={loading}>
              <Item
                dataField={'email'}
                editorType={'dxTextBox'}
                editorOptions={emailEditorOptions}
              >
                <RequiredRule message="Email is required"/>
                <EmailRule message="Email is invalid"/>
                <Label visible={false}/>
              </Item>
              <Item
                dataField={'password'}
                editorType={'dxTextBox'}
                editorOptions={passwordEditorOptions}
              >
                <RequiredRule message="Password is required"/>
                <Label visible={false}/>
              </Item>
              <Item
                dataField={'rememberMe'}
                editorType={'dxCheckBox'}
                editorOptions={rememberMeEditorOptions}
              >
                <Label visible={false}/>
              </Item>
              <ButtonItem horizontalAlignment="center">
                <ButtonOptions width={'50%'} type={'default'} useSubmitBehavior={true}>
                  <span className="dx-button-text">
                    {loading ? <LoadIndicator width={'24px'} height={'24px'} visible={true}/> : 'Sign In'}
                  </span>
                </ButtonOptions>
              </ButtonItem>
              <Item>
                <div className={'link'}>
                  <Link to={'/reset-password'}>Forgot password?</Link>
                </div>
              </Item>
              <Item>
                <div className={'link'}>
                  Don't have an account? <Link to={'/create-account'}>Create an account</Link>
                </div>
              </Item>
              {/* <ButtonItem>
          <ButtonOptions
            text={'Create an account'}
            width={'100%'}
            onClick={onCreateAccountClick}
          />
        </ButtonItem> */}
            </Form>
          </form>
        </div>
    }
  }

  return (
    <div>
      <div className="signin-screen-container">
      </div>
      <div className="signin-accordion-container">
        <h3>Sign In</h3>
        <h6>
          Access your GPT account.
        </h6>
        <br/>
        <div className="login-tabs-wrapper">
          <TabPanel
            width="100%"
            animationEnabled={true}
            swipeEnabled={true}
            dataSource={tabsDataSource}
            stylingMode="primary"
            iconPosition="start"
            itemComponent={TabPanelItem}
          />
        </div>
        <div className="login-footer-line">
          v250317 Copyright © <a href="https://www.botx.cloud" target="_blank" rel="noreferrer">BotX
          s.r.o.</a> {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}

const emailEditorOptions = {stylingMode: 'filled', placeholder: 'Email', mode: 'email'};
const passwordEditorOptions = {stylingMode: 'filled', placeholder: 'Password', mode: 'password'};
const rememberMeEditorOptions = {text: 'Remember me', elementAttr: {class: 'form-text'}};
